// Pure helpers for the info-view compare-items block, ported from the coffee
// InfoViewBlockCompareItems.Lib (presets / index calc) and TableView#render (prop building).

export const MAIN_ANALYSIS_MODE = 'main'

export const MAIN_PRESET_ID = 'top3'

export const PRESETS = [
  { id: 'top3', text: 'Top 3' },
  { id: 'top25', text: 'Top 25%' },
  { id: 'all', text: 'All' },
  { id: 'none', text: 'None' }
]

const ITEMS_PER_PAGE = 4

// Sort the given item indexes by an attribute value, honoring the reverse flag.
const sortIndexes = (items, indexes, attributeIndex, reverse) =>
  (indexes || []).slice().sort((a, b) => {
    const aAttribute = ((items[b] || {}).attributes || [])[attributeIndex] || {}
    const bAttribute = ((items[a] || {}).attributes || [])[attributeIndex] || {}
    const attr =
      undefined !== aAttribute.percents &&
      null !== aAttribute.percents &&
      undefined !== bAttribute.percents &&
      null !== bAttribute.percents
        ? 'percents'
        : 'amount'
    const aValue = aAttribute[attr] || 0
    const bValue = bAttribute[attr] || 0
    return reverse ? bValue - aValue : aValue - bValue
  })

// Indexes for a preset (top3 / top25 / all / none), ranked by the given attribute value.
// The reverse flag is applied later by sortIndexes, mirroring the coffee Lib.
export const calcIndexes = (items, preset, attributeIndex) => {
  if ('none' === preset) return []

  const ranked = items
    .map((item, index) => {
      const attribute = ((item || {}).attributes || [])[attributeIndex] || {}
      const key = undefined !== attribute.percents && null !== attribute.percents ? 'percents' : 'amount'
      return { amount: attribute[key], index }
    })
    .sort((a, b) => b.amount - a.amount)
    .map(item => item.index)

  switch (preset) {
    case 'top25':
      return ranked.slice(0, Math.round(items.length * 0.25))
    case 'top3':
      return ranked.slice(0, 3)
    default:
      return ranked
  }
}

export const getAttributeIndexByPosition = (position, attributes) =>
  attributes.findIndex(attribute => attribute.position === position)

// Resolved, sorted item indexes for a preset (or an explicit concept list when preset is empty).
// Ported from the coffee Lib.getItemsIndexes; also consumed by the pptx exporter via $STATS.
export const getItemsIndexes = (items = [], concepts, preset, attributeIndex, reverse) => {
  const indexes =
    '' !== preset
      ? calcIndexes(items, resolvePreset(preset, items.length), attributeIndex)
      : concepts.map(concept => items.findIndex(item => item.position === concept))
  return sortIndexes(items, indexes, attributeIndex, reverse)
}

// Whether a preset should be hidden given the item count (too few items to be meaningful).
const isPresetHidden = (presetId, itemsCount) =>
  ('top3' === presetId && 4 > itemsCount) || ('top25' === presetId && 3 > itemsCount)

export const resolvePreset = (preset, itemsCount) => (isPresetHidden(preset, itemsCount) ? 'all' : preset)

// Build the presets list the CompareItems DataView expects (each with an indexes() resolver).
const buildPresets = itemsCount =>
  PRESETS.map(preset => ({
    id: preset.id,
    text: preset.text,
    hidden: isPresetHidden(preset.id, itemsCount),
    indexes: (items, attributes, sortByAttributeIndex, reverseSortOrder = false) =>
      sortIndexes(items, calcIndexes(items, preset.id, sortByAttributeIndex), sortByAttributeIndex, reverseSortOrder)
  }))

// Assemble the derived CompareItems props from raw model data + resolved state.
// Coffee still supplies survey/report/export-coupled values (splitGroups, showExportButton,
// splitBy, callbacks) which pass straight through.
export const calcCompareItemsProps = ({
  format = {},
  items = [],
  attributes = [],
  concepts = [],
  preset,
  attribute,
  reverse,
  analysisMode,
  showImages,
  fullTexts
}) => {
  const resolvedShowImages = format.showImages && undefined !== showImages ? showImages : format.showImages
  const resolvedFullTexts = format.fullTexts && undefined !== fullTexts ? fullTexts : format.fullTexts

  const canUseImages =
    (undefined !== format.canUseImages ? format.canUseImages : true) && items.some(item => undefined !== item.image)

  return {
    enableSigtestAnalysis: undefined !== format.enableSigtestAnalysis ? format.enableSigtestAnalysis : true,
    enablePresets: undefined !== format.enablePresets ? format.enablePresets : true,
    enableRankBy: undefined !== format.enableRankBy ? format.enableRankBy : true,
    itemsPerPage: format.itemsPerPage || ITEMS_PER_PAGE,
    presets: buildPresets(items.length),
    presetIndex: PRESETS.findIndex(item => item.id === resolvePreset(preset, items.length)),
    selectedIndexes: '' !== preset ? null : concepts.map(v => items.findIndex(item => item.position === v)),
    sortByAttributeIndex: getAttributeIndexByPosition(attribute, attributes),
    analysisMode,
    reverseSortOrder: reverse,
    showImages: resolvedShowImages,
    fullTexts: resolvedFullTexts,
    canUseImages
  }
}
